<template>
  <div class="partnership-container">
    <snackbar
      v-if="snackbar.show"
      :error="snackbar.error"
      :success="snackbar.success"
      :message="snackbar.message"
      @close="resetSnackbar"
    />
    <div class="partnership-container__logo">
      <img src="@/assets/partners/playhub.png" alt="PlayHub Logo" />
    </div>
    <transition v-if="signUpSucceeded" name="fade" mode="out-in">
      <div class="partnership-container__success-container">
        <h1>
          {{ $t('partnerships.form.success.title') }}
        </h1>
        <p>
          {{ $t('partnerships.form.success.description') }}
        </p>
        <h2>
          {{ $t('partnerships.form.success.subtitle') }}
        </h2>
        <p>
          {{ $t('partnerships.form.success.stores') }}
        </p>
        <div class="partnership-container__success-container__store-buttons">
          <a rel="noopener" target="_black" href="https://apps.apple.com/BR/app/id613398383?mt=8" class="app-button">
            <img loading="lazy" width="148" height="50" alt="iOS's download button" src="@/assets/app-store.png" class="app-store">
          </a>
          <a rel="noopener" target="_black" href="https://play.google.com/store/apps/details?id=com.movile.playkids&amp;hl=pt_BR" class="app-button">
            <img loading="lazy" width="148" height="50" alt="Android's download button" src="@/assets/google-play.png" class="app-store">
          </a>
        </div>
      </div>
    </transition>
    <div v-else>
      <div v-if="!authenticated" class="partnership-container__header">
          <h1
            section-title v-html="this.newAccount ? $t('partnerships.form.success.signUpTitle') : $t('partnerships.form.success.signInTitle')"></h1>
          <p
            v-html="this.newAccount ? $t('partnerships.form.success.signUpDescriptionPlayHub') : $t('partnerships.form.success.signInDescription')"></p>
      </div>
      <div v-else class="partnership-container__header">
        <h1
            section-title v-html="this.newAccount ? $t('partnerships.form.success.signUpAuthenticatedTitle') : $t('partnerships.form.success.signInAuthenticatedTitle')"></h1>
          <p
            v-html="$t('partnerships.form.success.authenticatedDescription')"></p>
      </div>
      <transition name="fade" mode="out-in">
        <form v-if="authenticated" class="partnership-container__form" novalidate autocomplete="off">
          <div class="partnership-container__form__body">
            <div class="partnership-container__form__body__buttons">
              <btn
                class="partnership-container__form__body__buttons__button"
                color="white"
                @click="cancelSubscription"
              >{{ $t('partnerships.form.authenticated.cancelButton') }}</btn>
              <btn
                class="partnership-container__form__body__buttons__button"
                color="purple"
                submit
                :loading="form.processing"
                @click.prevent="activateSubscription"
              >{{ $t('partnerships.form.authenticated.confirmButton') }}</btn>
            </div>
          </div>
        </form>
        <div v-else>
          <form v-if="this.newAccount" class="partnership-container__form" novalidate autocomplete="off">
            <div class="partnership-container__form__body">
              <div class="partnership-container__form__body__input-group">
                <pk-input v-model="emailSignUp"
                :placeholder="$t('partnerships.form.signUp.inputEmail')"
                type="text" ref="emailSignUp"
                :disabled="form.processing"
                :error="$v.emailSignUp.$error"
                :errorText="$t('partnerships.form.errors.inputEmail')"
                />
                <div class="partnership-container__form__body__input-group__hint">
                  <small>O e-mail é apenas uma sugestão, você pode alterar digitando um novo de sua escolha.</small>
                </div>
              </div>
              <div class="partnership-container__form__body__input-group">
                <pk-input v-model="confirmEmail"
                  :placeholder="$t('partnerships.form.signUp.inputConfirmEmail')"
                  type="text" ref="confirmEmail"
                  :disabled="form.processing"
                  :error="$v.confirmEmail.$error"
                  :errorText="$t('partnerships.form.errors.inputConfirmEmail')"
                />

                <div
                  v-if="$v.confirmEmail.$error && !this.dontMatchEmail()"
                  class="partnership-container__form__body__input-group__hint hint-error"
                >
                  <small>{{ $t('partnerships.form.errors.inputConfirmEmail') }}</small>
                </div>

              </div>
              <div class="partnership-container__form__body__input-group">
                <pk-input v-model="password"
                  :placeholder="$t('partnerships.form.signUp.inputPassword')"
                  type="password" ref="password"
                  :disabled="form.processing"
                  :error="$v.password.$error"
                  :errorText="$t('partnerships.form.errors.inputPassword')"
                />
                <div class="partnership-container__form__body__input-group__hint">
                  <small>{{ $t('partnerships.form.signUp.inputPasswordHint') }}</small>
                </div>
              </div>
              <div class="partnership-container__form__body__input-group">
                <pk-input v-model="confirmPassword"
                  :placeholder="$t('partnerships.form.signUp.inputConfirmPassword')"
                  type="password" ref="confirmPassword"
                  :disabled="form.processing"
                  :error="$v.confirmPassword.$error"
                  :errorText="$t('partnerships.form.errors.inputConfirmPassword')"
                />
              </div>
              <div
                v-if="$v.confirmPassword.$error && !this.dontMatchPassword()"
                class="partnership-container__form__body__input-group__hint hint-error"
              >
                <small>{{ $t('partnerships.form.errors.inputConfirmPassword') }}</small>
              </div>
              <div class="partnership-container__form__body__terms">
                <consent-box v-model="consentPolicy" :shake="shakeBoxPrivacy">
                  {{ $t('policies.privacy') }}
                  <br />
                  <a :href="`https://policies.playkidsapp.com/pt/privacy/${this.consentVersion}`" target="_blank">
                    {{ $t('policies.privacyTitle') }}
                  </a>
                </consent-box>
                <consent-box v-model="consentTerms" :shake="shakeBoxTerms">
                  {{ $t('policies.tos') }}
                  <br />
                  <a :href="`https://policies.playkidsapp.com/pt/tos/${this.consentVersion}`" target="_blank">
                    {{ $t('policies.tosTitle') }}
                  </a>
                </consent-box>
              </div>
              <div class="partnership-container__form__body__buttons">
                <btn
                  class="partnership-container__form__body__buttons__button"
                  color="purple"
                  submit
                  :loading="form.processing"
                  @click.prevent="signUp"
                >{{ $t('partnerships.form.signUp.cta') }}</btn>
              </div>
              <div class="partnership-container__form__body__signIn-option">
                {{ $t('partnerships.form.signInOption.title') }}
                <a @click.prevent="toogleNewAccountForm">
                  {{ $t('partnerships.form.signInOption.cta') }}
                </a>
              </div>
            </div>
          </form>
          <form v-else class="partnership-container__form" @submit.prevent="signIn" novalidate autocomplete="off">
            <div class="partnership-container__form__body__input-group">
              <pk-input v-model="email"
                :placeholder="$t('partnerships.form.signIn.inputEmail')"
                type="text" ref="email"
                :disabled="form.processing"
                :error="$v.email.$error"
                :errorText="$t('partnerships.form.errors.inputEmail')"
              />
            </div>
            <div class="partnership-container__form__body__input-group">
              <pk-input v-model="password"
                :placeholder="$t('partnerships.form.signIn.inputPassword')"
                type="password" ref="password"
                :disabled="form.processing"
                autocomplete="off"
                :error="$v.password.$error"
              />
            </div>
            <div class="partnership-container__form__body__buttons">
              <btn
                class="partnership-container__form__body__buttons__button"
                color="purple"
                submit
                :loading="form.processing"
              >{{ $t('partnerships.form.signIn.cta') }}</btn>
            </div>
            <div class="partnership-container__form__body__signUp-option">
              {{ $t('partnerships.form.signUpOption.title') }}
              <a @click.prevent="toogleNewAccountForm">
                {{ $t('partnerships.form.signUpOption.cta') }}
              </a>
            </div>
          </form>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { strongPassword } from '@/js/validationUtils'
import { required, email } from 'vuelidate/lib/validators'
import { AuthenticationPlatform } from '@/api/peanutbutter'
import authenticationService from '@/js/AuthenticationService'

import validatorMixin from '@/js/mixins/validator'
import integrationACLAPI from '@/api/integration-acl'
import NewResponseError from '@/api/http/NewResponseError'
import { latestConsentVersion, ConsentVersionAppMap } from '@/js/consentVersionMap'

const errorText = {
  notFound: 'Ops. Não encontramos uma conta PlayKids para o seu plano.<br />Por favor, entre em contato com a sua operadora para verificar a ativação da sua conta',
  wrongCredentials: 'Email não cadastrado ou senha incorreta.<br />Por favor, tente novamente.',
  emailAlreadyExists: 'Já encontramos uma conta PlayKids cadastrada neste email',
  generic: 'Ops! Algo não funcionou como esperávamos enquanto validávamos seu cadastro.<br />Por favor, entre em contato com sua operadora.'
}

export default {
  name: 'playhub-account',
  mixins: [validatorMixin],
  data: () => ({
    signUpSucceeded: false,
    newAccount: true,
    authenticated: false,
    accountId: null,
    form: {
      processing: false
    },
    snackbar: {
      show: false,
      message: '',
      error: true,
      success: false
    },
    code: '',
    email: '',
    emailSignUp: '',
    confirmEmail: '',
    planId: '',
    externalId: '',
    login: '',
    password: '',
    confirmPassword: '',
    consentVersion: latestConsentVersion,
    consentPolicy: false,
    consentTerms: false,
    shakeBoxPrivacy: false,
    shakeBoxTerms: false,
    submitted: false
  }),
  mounted() {
    this.emailSignUp = this.$store.state.email
  },
  validations() {
    const validations = {}
    const matchEmail = this.dontMatchEmail
    const matchPassword = this.dontMatchPassword
    if (this.authenticated) {
      validations.code = { }
    } else {
      validations.password = { required }
      if (this.newAccount) {
        validations.emailSignUp = { required, email }
        validations.confirmEmail = { required, email, matchEmail }
        validations.confirmPassword = { required, strongPassword, matchPassword }
      } else {
        validations.email = { required, email }
      }
    }
    return validations
  },
  methods: {
    toogleNewAccountForm() {
      this.newAccount = !this.newAccount
    },
    dontMatchEmail() {
      return (this.emailSignUp === this.confirmEmail)
    },
    dontMatchPassword() {
      return this.password === this.confirmPassword
    },
    verifyPolicyConsent() {
      this.shakeBoxPrivacy = false
      this.shakeBoxTerms = false

      if (!this.consentPolicy) {
        this.$nextTick(() => {
          this.shakeBoxPrivacy = true
        })
      }

      if (!this.consentTerms) {
        this.$nextTick(() => {
          this.shakeBoxTerms = true
        })
      }

      if (!this.consentPolicy || !this.consentTerms) {
        return false
      }

      return true
    },
    cancelSubscription() {
      this.resetData()
    },
    resetData() {
      this.newAccount = false
      this.authenticated = false
      this.accountId = null
      this.planId = ''
      this.externalId = ''
      this.login = ''
      this.code = ''
      this.email = ''
      this.emailSignUp = ''
      this.confirmEmail = ''
      this.password = ''
      this.confirmPassword = ''
      this.consentPolicy = false
      this.consentTerms = false
    },
    resetSnackbar() {
      this.snackbar = {
        show: false,
        message: ``,
        success: false,
        error: false
      }
    },
    async signIn() {
      this.resetSnackbar()
      if (!this.isValid()) {
        return
      }
      try {
        this.form.processing = true

        const credentials = {
          email: this.email,
          password: this.password
        }

        await authenticationService.signIn({
          signInParameters: {
            authenticationPlatform: AuthenticationPlatform.EMAIL,
            credentials
          }
        })

        this.authenticated = true
        this.$emit('authenticated')
      } catch (error) {
        this.snackbar = {
          show: true,
          message: error.code === 403 ? errorText.wrongCredentials : errorText.generic,
          success: false,
          error: true
        }
      } finally {
        this.form.processing = false
        this.$nextTick(() => {
          this.password = ''
          this.$v.$reset()
        })
      }
    },
    async signUp() {
      this.resetSnackbar()
      if (!this.isValid() || !this.verifyPolicyConsent()) {
        return
      }
      try {
        this.form.processing = true

        const credentials = {
          email: this.emailSignUp,
          password: this.password
        }
        const privacyPreferences = {
          privacyPolicy: {
            consent: this.consentPolicy,
            version: ConsentVersionAppMap[this.consentVersion]
          },
          termsOfUse: {
            consent: this.consentTerms,
            version: ConsentVersionAppMap[this.consentVersion]
          }
        }

        const data = {
          credentials,
          extraData: {},
          authenticationPlatform: AuthenticationPlatform.EMAIL,
          privacyPreferences
        }

        try {
          await authenticationService.signUp(data)

          this.authenticated = true
          this.$emit('authenticated')
        } catch (err) {
          if (err.code) {
            throw err
          }
          this.errorCode = 400
        }
      } catch (error) {
        this.snackbar = {
          show: true,
          message: error.code === 409 ? errorText.emailAlreadyExists : errorText.generic,
          success: false,
          error: true
        }
      } finally {
        this.form.processing = false
        this.$nextTick(() => {
          this.password = ''
          this.confirmPassword = ''
          this.$v.$reset()
        })
      }
    },
    async activateSubscription() {
      this.resetSnackbar()
      try {
        if (!this.isValid()) {
          return
        }
        this.form.processing = true
        this.login = this.$store.state.login
        this.externalId = this.$store.state.externalId
        this.planId = this.$store.state.planId
        this.accountId = this.$store.state.user.accountId
        await integrationACLAPI.activateExternalId(this.externalId, this.planId, this.accountId, this.login)
        this.signUpSucceeded = true
        this.resetData()
      } catch (error) {
        let errorBody = ''
        let errorCode = ''
        if (error instanceof NewResponseError) {
          errorBody = error.body.Mensagem
          errorCode = error.body.Codigo
        } else {
          errorBody = error.message
        }
        this.snackbar = {
          show: true,
          message: error.body && errorBody ? `${errorBody} (Error code: ${errorCode})` : errorText.generic,
          success: false,
          error: true
        }
      } finally {
        this.form.processing = false
      }
    },
    async createSubscription() {
      this.resetSnackbar()
      try {
        if (!this.isValid()) {
          return
        }

        this.form.processing = true
        await integrationACLAPI.createPlayHubSubscription(this.accountId, this.code)

        this.signUpSucceeded = true
        this.resetData()
      } catch (error) {
        this.snackbar = {
          show: true,
          message: error.code === 404 ? errorText.notFound : errorText.generic,
          success: false,
          error: true
        }
      } finally {
        this.form.processing = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.partnership-container {
  position: relative;
  flex-grow: 1;
  overflow: visible;
  padding: 0 4rem;

  &__logo {
    display: flex;
    justify-content: center;
    min-width: 100%;
    margin-top: 22px;
    margin-bottom: 4rem;

    img {
      max-width: 161px;
      max-height: 163px;
    }

    @media screen and (min-width: 641px) {
      img {
        max-width: 201px;
        max-height: 203px;
      }
    }
  }
  &__header {
    min-width: 100%;
    margin-bottom: 2rem;
    h1 {
      font-family: Nunito;
      font-style: normal;
      font-weight: 900;
      font-size: 26px;
      line-height: 32px;
      color: #443D4B;
    }
    p {
      font-family: Nunito;
      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: #454545;
    }

    @media screen and (min-width: 641px) {
      h1 {
        max-width: 95%;
      }
      p {
        max-width: 95%;
      }
    }
  }

  &__success-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 8rem;
    text-align: center;

    h1 {
      max-width: 500px;
      font-size: 2em;
      margin-bottom: 2rem;
    }
    h2 {
      max-width: 500px;
      font-size: 1.5em;
      margin-bottom: 2rem;
    }
    p {
      max-width: 600px;
      font-size: 1.3em;
      margin-bottom: 1rem;
    }

    &__store-buttons {
      display: flex;
      justify-content: center;
      margin-top: 3rem;

      a {
        margin: 0 1rem 0 1rem;
      }
    }
  }

  &__form {
    width: 100%;
    max-width: 480px;
    margin-bottom: 6rem;
    &__body {
      min-width: 100%;
      &__input-group {
        margin-bottom: 1rem;
      }
      &__bind-title {
        margin-top: 3rem;
        font-family: Nunito;
        font-style: normal;
        font-weight: 900;
        font-size: 20px;
        line-height: 26px;
        color: #443D4B;
      }
      &__terms {
        margin-top: 2rem;
      }
      &__info {
        display: flex;
      }
      &__buttons {
        display: flex;
        &__button {
          flex: 1;
          margin-top: 2rem;
        }
      }
      &__signUp-option, &__signIn-option {
        display: flex;
        justify-content: center;
        margin-top: 3rem;
        a {
          margin-left: 0.4rem;
        }
      }

      .hint-error {
        color: $error-color;
      }
    }
  }
}
</style>
